import { Outlet, NavLink, Navigate } from "react-router-dom";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { LayoutDashboard, Package, ClipboardList, MessageSquare, Settings, LogOut } from "lucide-react";

const navItems = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "admin-nav-dashboard-tab" },
  { to: "/admin/products", label: "Products", icon: Package, testid: "admin-nav-products-tab" },
  { to: "/admin/orders", label: "Orders", icon: ClipboardList, testid: "admin-nav-orders-tab" },
  { to: "/admin/chat", label: "Live Chat", icon: MessageSquare, testid: "admin-nav-chat-tab" },
  { to: "/admin/settings", label: "Settings", icon: Settings, testid: "admin-nav-settings-tab" },
];

export default function AdminLayout() {
  const { admin, loading, logout } = useAdminAuth();

  if (loading) {
    return <div className="min-h-screen bg-ink flex items-center justify-center text-muted2">Loading…</div>;
  }
  if (!admin) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="min-h-screen bg-ink text-cream flex">
      <aside className="w-64 shrink-0 border-r border-gold/20 bg-ink-800 hidden md:flex flex-col">
        <div className="p-6 border-b border-gold/20">
          <span className="font-serif text-xl text-cream">Idea<span className="text-gold"> Events</span></span>
          <p className="overline-label mt-1 text-[10px]">Admin</p>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${isActive ? "bg-gold-deep text-gold border border-gold/40" : "text-cream/70 hover:text-gold hover:bg-ink-700"}`}>
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-gold/20">
          <div className="px-2 pb-3">
            <p className="text-sm text-cream truncate">{admin.name}</p>
            <p className="text-xs text-muted2 truncate">{admin.email}</p>
            <span className="inline-block mt-2 text-[10px] font-mono uppercase tracking-wider text-gold border border-gold/40 rounded-full px-2 py-0.5">{admin.role}</span>
          </div>
          <button data-testid="admin-logout" onClick={logout} className="btn-silver w-full !py-2 text-xs">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="md:hidden flex items-center justify-between p-4 border-b border-gold/20 bg-ink-800">
          <span className="font-serif text-lg text-cream">Idea<span className="text-gold"> Events</span> Admin</span>
          <button onClick={logout} className="text-gold"><LogOut className="w-5 h-5" /></button>
        </div>
        <div className="md:hidden flex overflow-x-auto gap-2 p-3 border-b border-gold/20 bg-ink-800">
          {navItems.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) => `whitespace-nowrap px-3 py-1.5 rounded-full text-xs ${isActive ? "bg-gold text-ink" : "bg-ink-700 text-cream/70"}`}>
              {n.label}
            </NavLink>
          ))}
        </div>
        <main className="flex-1 overflow-y-auto p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
